import { PATHS } from '../config';
import { dynoapeAPI } from '../dynoape';
import { OrgEmployeeRequestService } from '../OrgEmployeeRequestService';
import type {
    EmployeeRequest,
    EmployeeRequestCommentRequest,
    EmployeeRequestCreateTask,
    EmployeeRequestPatternRequest,
    EmployeeRequestsMultipleRequest,
    EmployeeRequestStatusRequest,
} from './Models';

const postEmployeeRequests = async (
    departmentId: string,
    payload: EmployeeRequestsMultipleRequest,
): Promise<Record<string, EmployeeRequest[]>> => {
    const response = await dynoapeAPI.post(
        PATHS.departmentEmployeeRequestsMultiple(departmentId),
        payload,
    );

    return response?.elements;
};

const putEmployeeRequestPattern = async (
    departmentId: string,
    employeeId: string,
    requestId: string,
    payload: EmployeeRequestPatternRequest,
): Promise<EmployeeRequest> =>
    await dynoapeAPI.put(
        PATHS.departmentEmployeeRequest(departmentId, employeeId, requestId),
        payload,
    );

const putEmployeeRequestStatus = async (
    departmentId: string,
    employeeId: string,
    requestId: string,
    payload: EmployeeRequestStatusRequest,
): Promise<EmployeeRequest> =>
    await dynoapeAPI.put(
        PATHS.departmentEmployeeRequestStatus(
            departmentId,
            employeeId,
            requestId,
        ),
        payload,
    );

const putEmployeeRequestComment = async (
    departmentId: string,
    employeeId: string,
    requestId: string,
    payload: EmployeeRequestCommentRequest,
): Promise<EmployeeRequest> =>
    await dynoapeAPI.put(
        PATHS.departmentEmployeeRequestComment(
            departmentId,
            employeeId,
            requestId,
        ),
        payload,
    );

/*
 * Creates employee requests across both scopes:
 * org-scoped sources are posted once to the org endpoint, while
 * department-scoped sources are posted per department.
 *
 * The requests are sent sequentially: concurrent mutations refresh the
 * shared CSRF token and invalidate each other (403 Forbidden).
 *
 * Throws when the creation fails (nothing to create, or any request
 * failing), so that callers relying on a returned response treat
 * it as a failed creation.
 */
const createEmployeeRequests = async (
    orgId: string,
    createTasks: EmployeeRequestCreateTask[],
): Promise<Record<string, EmployeeRequest[]>[]> => {
    const executableTasks = createTasks.filter(
        (task) => task.employeeEntityIds.length > 0,
    );

    if (!executableTasks.length) {
        // No requests could be created for the selected employees and request types.
        throw new Error(
            'Ingen innspill kunne opprettes for de valgte ansatte og typene.',
        );
    }

    const createdRequests: Record<string, EmployeeRequest[]>[] = [];

    for (const task of executableTasks) {
        const payload: EmployeeRequestsMultipleRequest = {
            employeeEntityIds: task.employeeEntityIds,
            putRequest: task.putRequest,
        };

        const response =
            task.scope === 'org'
                ? await OrgEmployeeRequestService.postOrgEmployeeRequests(
                      orgId,
                      payload,
                  )
                : await postEmployeeRequests(task.departmentId, payload);

        // The API client swallows request errors and returns undefined
        if (response === undefined) {
            // Some of the requests or THE request could not be created.
            throw new Error(
                executableTasks.length === 1
                    ? 'Innspillet kunne ikke opprettes.'
                    : 'Noen av innspillene kunne ikke opprettes.',
            );
        }

        createdRequests.push(response);
    }

    return createdRequests;
};

const EmployeeRequestService = {
    createEmployeeRequests,
    postEmployeeRequests,
    putEmployeeRequestPattern,
    putEmployeeRequestStatus,
    putEmployeeRequestComment,
};

export default EmployeeRequestService;
